export type ChangeType =
  | "Added"
  | "Changed"
  | "Fixed"
  | "Removed"
  | "Security"
  | "Deprecated";

export const CHANGE_TYPES: ChangeType[] = [
  "Added",
  "Changed",
  "Fixed",
  "Removed",
  "Security",
  "Deprecated",
];

export interface ChangelogEntry {
  text: string;
  type: ChangeType;
  breaking: boolean;
  commits: string[];
}

export interface ChangelogSection {
  type: ChangeType;
  entries: ChangelogEntry[];
}

export interface ChangelogVersion {
  version: string;
  date: string;
  rawDate: string;
  sections: ChangelogSection[];
  totalEntries: number;
}

const VERSION_HEADING = /^##\s+\[([^\]]+)\]\s*(?:—|-|–)?\s*(.*)$/;
const SECTION_HEADING = /^###\s+(.+)$/;
const BULLET = /^[-*]\s+(.+)$/;
const COMMIT_REF = /\b([0-9a-f]{7,40})\b/g;
const BREAKING_TAG = /\*\*BREAKING\*\*/i;

function formatDate(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!iso) return trimmed;
  const date = new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3])));
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function parseEntry(line: string, type: ChangeType): ChangelogEntry {
  const breaking = BREAKING_TAG.test(line);
  const commits: string[] = [];
  let match: RegExpExecArray | null;
  COMMIT_REF.lastIndex = 0;
  while ((match = COMMIT_REF.exec(line)) !== null) {
    commits.push(match[1]);
  }
  const text = line.replace(BREAKING_TAG, "").trim();
  return { text, type, breaking, commits };
}

export function parseChangelog(markdown: string): ChangelogVersion[] {
  const lines = markdown.split(/\r?\n/);
  const versions: ChangelogVersion[] = [];

  let currentVersion: ChangelogVersion | null = null;
  let currentSection: ChangelogSection | null = null;

  for (const rawLine of lines) {
    const line = rawLine.replace(/\s+$/, "");

    const versionMatch = VERSION_HEADING.exec(line);
    if (versionMatch) {
      if (currentVersion) {
        currentVersion.totalEntries = currentVersion.sections.reduce(
          (sum, s) => sum + s.entries.length,
          0,
        );
        versions.push(currentVersion);
      }
      const rawDate = (versionMatch[2] ?? "").trim();
      currentVersion = {
        version: versionMatch[1].trim(),
        rawDate,
        date: formatDate(rawDate),
        sections: [],
        totalEntries: 0,
      };
      currentSection = null;
      continue;
    }

    if (!currentVersion) continue;

    const sectionMatch = SECTION_HEADING.exec(line);
    if (sectionMatch) {
      const heading = sectionMatch[1].trim();
      const type = CHANGE_TYPES.find(
        (t) => t.toLowerCase() === heading.toLowerCase(),
      );
      if (!type) {
        currentSection = null;
        continue;
      }
      currentSection = { type, entries: [] };
      currentVersion.sections.push(currentSection);
      continue;
    }

    const bulletMatch = BULLET.exec(line);
    if (bulletMatch && currentSection) {
      currentSection.entries.push(parseEntry(bulletMatch[1], currentSection.type));
    }
  }

  if (currentVersion) {
    currentVersion.totalEntries = currentVersion.sections.reduce(
      (sum, s) => sum + s.entries.length,
      0,
    );
    versions.push(currentVersion);
  }

  return versions;
}
